import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  NgZone
} from '@angular/core';
import { GestureController } from '@ionic/angular';
import type { Gesture } from '@ionic/core';

export interface SliderItem {
  id: number | string;
  attachment?: string;
  title?: string;
  slug?: string;
  description?: string;
  is_active?: boolean;
}

@Component({
  selector: 'app-content-slider',
  templateUrl: './content-slider.component.html',
  styleUrls: ['./content-slider.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContentSliderComponent implements AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('sliderContainer') sliderContainer!: ElementRef<HTMLElement>;

  @Input() items: SliderItem[] = [];
  @Input() selectedId: number | string | null = null;
  @Input() isRTL: boolean = false;
  @Input() autoSlide: boolean = false;
  @Input() autoSlideInterval: number = 5000;
  @Input() swipeThresholdPx: number = 40;
  @Input() slideChangeAnimationPrevClass: string = 'animClassPrev';
  @Input() slideChangeAnimationNextClass: string = 'animClassNext';
  @Input() slideChangeAnimationResetMs: number = 500;

  @Output() itemSelected = new EventEmitter<SliderItem>();

  public orderedItems: SliderItem[] = [];
  public activeIndex: number = 0;
  public inTransit: boolean = false;
  public isNext: boolean = true;
  public dragOffset = 0;
  public dragProgress = 0;
  public dragProgressAbs = 0;

  private autoSlideTimer?: any;
  private gesture?: Gesture;

  constructor(
    private gestureCtrl: GestureController,
    private cdRef: ChangeDetectorRef,
    private ngZone: NgZone
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items'] && this.items) {
      this.orderedItems = [...this.items];
      this.syncActiveIndex();
    }
    if (changes['selectedId'] && !changes['selectedId'].firstChange) {
      this.syncActiveIndex();
    }
  }

  ngAfterViewInit(): void {
    this.initSwipeGesture();
    if (this.autoSlide) this.startAutoSlide();
    
    // Auto-select and emit the first item if no selection is made
    if (this.orderedItems.length > 0 && this.selectedId === null) {
      this.selectedId = this.orderedItems[0].id;
      this.itemSelected.emit(this.orderedItems[0]);
      this.cdRef.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this.stopAutoSlide();
    this.gesture?.destroy();
  }

  trackById = (index: number, item: SliderItem): number | string => item.id;

  private initSwipeGesture(): void {
    if (!this.sliderContainer?.nativeElement) return;
    const width = window.innerWidth;

    this.gesture = this.gestureCtrl.create({
      el: this.sliderContainer.nativeElement,
      gestureName: 'swipe-drag',
      threshold: 0,
      onStart: () => {
        this.stopAutoSlide();
        this.sliderContainer.nativeElement.style.setProperty('--transition-speed', '0ms');
      },
      onMove: (detail) => {
        this.dragOffset = detail.deltaX;
        this.dragProgress = detail.deltaX / width; 
        this.dragProgressAbs = Math.abs(this.dragProgress);
        this.cdRef.markForCheck();
      },
      onEnd: (detail) => {
        this.sliderContainer.nativeElement.style.setProperty('--transition-speed', `${this.slideChangeAnimationResetMs}ms`);
        
        if (Math.abs(detail.deltaX) > this.swipeThresholdPx) {
          detail.deltaX < 0 ? this.next() : this.prev();
        }

        this.dragOffset = 0;
        this.dragProgress = 0;
        this.dragProgressAbs = 0;
        this.cdRef.markForCheck();
        
        if (this.autoSlide) this.restartAutoSlide();
      }
    });
    this.gesture.enable(true);
  }

  private syncActiveIndex(): void {
    if (this.selectedId !== null) {
      const index = this.orderedItems.findIndex(item => item.id === this.selectedId);
      if (index !== -1) {
        this.activeIndex = index;
        this.cdRef.markForCheck();
      }
    }
  }

  public next(): void {
    if (this.inTransit || this.orderedItems.length < 2) return;
    this.isNext = true;
    this.inTransit = true;
    this.activeIndex = (this.activeIndex + 1) % this.orderedItems.length;
    this.emitAndLock();
  }

  public prev(): void {
    if (this.inTransit || this.orderedItems.length < 2) return;
    this.isNext = false;
    this.inTransit = true;
    this.activeIndex = (this.activeIndex - 1 + this.orderedItems.length) % this.orderedItems.length;
    this.emitAndLock();
  }

  public onItemClick(item: SliderItem): void {
    // Always emit the selected item, no need to check if it's already active
    const clickedIndex = this.orderedItems.findIndex(i => i.id === item.id);
    if (clickedIndex !== -1 && clickedIndex !== this.activeIndex) {
      this.goToIndex(clickedIndex);
    } else {
      // Even if it's already active, emit it again to ensure selection
      this.selectedId = item.id;
      this.itemSelected.emit(item);
    }
  }

  private goToIndex(index: number): void {
    if (index === this.activeIndex) return;
    index > this.activeIndex ? this.next() : this.prev();
  }

  private emitAndLock(): void {
    const activeItem = this.orderedItems[this.activeIndex];
    this.selectedId = activeItem.id;
    this.itemSelected.emit(activeItem);
    this.cdRef.markForCheck();

    setTimeout(() => {
      this.inTransit = false;
      this.cdRef.markForCheck();
    }, this.slideChangeAnimationResetMs);

    if (this.autoSlide) this.restartAutoSlide();
  }

  private startAutoSlide(): void {
    this.stopAutoSlide();
    this.ngZone.runOutsideAngular(() => {
      this.autoSlideTimer = setInterval(() => {
        this.ngZone.run(() => this.next());
      }, this.autoSlideInterval);
    });
  }

  private stopAutoSlide(): void {
    if (this.autoSlideTimer) clearInterval(this.autoSlideTimer);
  }

  private restartAutoSlide(): void {
    this.stopAutoSlide();
    this.startAutoSlide();
  }
}